import Plate from 'atoms/Plate';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useStore } from 'stores/store';
import { logEvent, reportEvent } from 'utils/analytics';
import { IFilterData, IInvestment } from 'utils/globalTypes';
import { useMobileDetect } from 'utils/hooks/useMobileDetect';
import { navigateToOffering } from 'utils/navigateUtils';
import { ISearch } from 'utils/networkRequests';
import { getSingleValue } from 'utils/urlUtils';

import s from './OfferingSearch.module.scss';
import OfferingSearchInput from './OfferingSearchInput';

const BrowsingHeaderModal = dynamic(() => import('molecules/BrowsingHeaderModal'));
const OfferingSearchRecent = dynamic(() => import('./OfferingSearchRecent'));
const OfferingSearchNoResults = dynamic(() => import('./OfferingSearchNoResults'));
const OfferingSearchSuggestions = dynamic(() => import('./OfferingSearchSuggestions'));

interface ISearchProps {
  minimal?: boolean;
  requestCmd?: string;
  filter?: (item: IFilterData) => boolean;
  onSelect?: (search?: ISearch) => void;
  onSearch?: (search?: ISearch) => void;
  defaultSearchTerm?: string;
}

const OfferingSearch = ({
  minimal,
  filter,
  requestCmd,
  defaultSearchTerm,
  onSelect,
  onSearch
}: ISearchProps) => {
  const store = useStore();
  const [isFocused, setIsFocused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { isMobile } = useMobileDetect();

  // used to set the search input in focus after the clear button
  const searchInput = useRef<HTMLInputElement>(null);
  const searchInputModal = useRef<HTMLInputElement>(null);

  const router = useRouter();

  useEffect(() => {
    setSearchTerm(defaultSearchTerm || '');
    store.SearchStore.setCurrentSearchTerm(defaultSearchTerm);
  }, [defaultSearchTerm, store.SearchStore]);

  useEffect(() => {
    const searchTerm = getSingleValue(router?.query?.t);
    if (searchTerm) {
      setSearchTerm(searchTerm);
      store.SearchStore.setCurrentSearchTerm(searchTerm);
    }
  }, [router?.isReady, router?.query?.t, store.SearchStore]);

  const onSubmitSelect = (item: IInvestment, action: string) => {
    if (item?.offering_id) {
      logEvent('select', action, item?.offering_id);

      if (onSelect) {
        setSearchTerm(item?.offering_name || item?.offering_id);
        onSelect({ offeringId: item?.offering_id });
      } else {
        navigateToOffering(item?.offering_id);
      }
    }
  };

  const onChangeValue = (value: string) => {
    setShowResults(true);
    setSearchTerm(value);
    store.SearchStore.setCurrentSearch(value, store.MarketplaceStore.getFilters, requestCmd);
  };

  const onSubmitSearch = (searchValue?: string, searchType?: string) => {
    reportEvent('search', { search_term: searchValue || '', search_type: searchType });

    setSearchTerm(searchValue || '');
    store.MarketplaceStore.setOfferingSearchTerm(searchValue || '');
    if (onSearch) {
      onSearch();
    } else {
      store.MarketplaceStore.setAssets();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSubmitSearch(searchTerm, 'enter');
      setShowResults(false);
      if (isMobile) {
        setIsFocused(false);
      }
    } else if (event.key == 'Escape') {
      setShowResults(false);
    }
  };

  const clearTerms = () => {
    store.SearchStore.clearSearch();
    onSubmitSearch('', 'clear');
  };

  const setFocus = () => {
    if (!isFocused) {
      setIsFocused(true);
      setShowResults(true);
    }
  };

  const clearFocus = () => {
    // only clear the focus if we didnt get back to focus
    if (
      document.activeElement !== searchInput?.current &&
      document.activeElement !== searchInputModal?.current
    ) {
      setIsFocused(false);
      setShowResults(false);
      searchInput?.current?.blur();
    }
  };

  const recentSearches = store.ActivityStore.recentSearches;
  const recentItems = store.ActivityStore.recentItems;

  const { locations, suggestions } = store.SearchStore.getSearchItems;
  let locationItems =
    locations && !isEmpty(locations) ? locations.filter((item) => !filter || filter(item)) : [];

  const places = locationItems.map((item) => String(item.formatted_place));
  locationItems = locationItems.filter(
    (value, index) => places.indexOf(value.formatted_place) === index
  );

  let suggestionItems =
    suggestions && !isEmpty(suggestions)
      ? suggestions
          .filter((item) => !filter || filter(item))
          .slice(0, locations?.length ? 10 - locations.length : 10)
      : [];

  const locationsLength = suggestionItems?.length >= 5 ? 5 : 10 - (suggestionItems?.length || 0);
  const suggestionsLength = locationItems?.length >= 5 ? 5 : 10 - (locationItems?.length || 0);
  locationItems = locationItems.slice(0, locationsLength);
  suggestionItems = suggestionItems.slice(0, suggestionsLength);

  // show suggestions if we have a search term, and we have suggestions
  const showSuggestions =
    isFocused &&
    showResults &&
    searchTerm &&
    (!isEmpty(locationItems) || !isEmpty(suggestionItems));

  // show no suggestions if we have a search term, and we have no suggestions
  const showNoSuggestions =
    isFocused &&
    showResults &&
    searchTerm &&
    store.SearchStore.getIsSearching &&
    isEmpty(locationItems) &&
    isEmpty(suggestionItems);

  // show recent searches/items if we don't have a search term and we have recent searches/items
  const showRecentSearches =
    isFocused &&
    showResults &&
    !searchTerm &&
    (recentSearches?.length > 0 || recentItems?.length > 0);

  return (
    <div className={cn(s.OfferingSearch, { minimal })}>
      <OfferingSearchInput
        searchButton
        cancelButton
        onSearchClick={() => onSubmitSearch(searchTerm, 'icon')}
        onCancelClick={() => clearTerms()}
        ref={searchInput}
        searchTerm={searchTerm}
        minimal={minimal}
        placeholder={minimal ? '' : 'Name, Location'}
        onChange={onChangeValue}
        onMouseDown={setShowResults}
        showRecent={showResults}
        onKeyDown={onKeyDown}
        onSubmit={onSubmitSearch}
        onFocus={() => {
          setFocus();
          if (isMobile) {
            searchInput?.current?.blur();
          }
        }}
        onBlur={() => setTimeout(() => clearFocus(), 200)}
      />

      {isMobile && (
        <BrowsingHeaderModal
          isShown={isFocused}
          onClose={() => setIsFocused(false)}
          isSearch
          heading={
            <OfferingSearchInput
              cancelButton
              onCancelClick={() => setSearchTerm('')}
              ref={searchInputModal}
              isFocused={isFocused}
              searchTerm={searchTerm}
              minimal={minimal}
              placeholder={minimal ? '' : 'Name, State, City'}
              onChange={onChangeValue}
              onMouseDown={!(isFocused && showResults) ? setShowResults : undefined}
              showRecent={showResults}
              onKeyDown={onKeyDown}
              onSubmit={onSubmitSearch}
            />
          }
        >
          {showSuggestions && (
            <OfferingSearchSuggestions
              locationItems={locationItems}
              onSubmitSearch={onSubmitSearch}
              suggestionItems={suggestionItems}
              onSubmitSelect={onSubmitSelect}
            />
          )}
          {showNoSuggestions && <OfferingSearchNoResults searchTerm={searchTerm} />}
          {showRecentSearches && (
            <OfferingSearchRecent
              recentSearches={recentSearches}
              onSubmitSearch={onSubmitSearch}
              recentItems={recentItems}
              onSubmitSelect={onSubmitSelect}
              searchTerm={searchTerm}
            />
          )}
        </BrowsingHeaderModal>
      )}

      {showSuggestions && !isMobile && (
        <Plate isSecondary isVerticalSpaced>
          <OfferingSearchSuggestions
            locationItems={locationItems}
            onSubmitSearch={onSubmitSearch}
            suggestionItems={suggestionItems}
            onSubmitSelect={onSubmitSelect}
          />
        </Plate>
      )}
      {showNoSuggestions && !isMobile && (
        <Plate isSecondary isVerticalSpaced>
          <OfferingSearchNoResults searchTerm={searchTerm} />
        </Plate>
      )}
      {showRecentSearches && !isMobile && (
        <Plate isSecondary isVerticalSpaced>
          <OfferingSearchRecent
            recentSearches={recentSearches}
            onSubmitSearch={onSubmitSearch}
            recentItems={recentItems}
            onSubmitSelect={onSubmitSelect}
            searchTerm={searchTerm}
          />
        </Plate>
      )}
    </div>
  );
};

export default observer(OfferingSearch);
